"use client";

import { useState, useContext } from "react";
import { ModalData } from "@/app/(workspace)/_hooks/useModal";
import { Button } from "@/components/ui/Button";
import { updateProjectTitle } from "@/lib/firebase/services/firestore";
import { ToastContext } from "@/providers/ToastProvider";

const INPUT_BASE_STYLE =
  "w-full px-4 py-2.5 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-sm text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 focus:border-transparent transition-colors";

export function EditTitleModal({
  data,
  onClose,
}: {
  data: ModalData;
  onClose: () => void;
}) {
  const toastContext = useContext(ToastContext);
  const { projectId, currentTitle, onTitleUpdate } = data;
  const [title, setTitle] = useState(currentTitle || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    // 유효성 검사
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    if (!projectId) {
      setError("Project ID is missing");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      await updateProjectTitle(projectId, title.trim());

      // 부모 컴포넌트에 알림
      if (onTitleUpdate) {
        onTitleUpdate(title.trim());
      }

      // 토스트 먼저 표시 (모달 닫기 전에)
      toastContext?.showToast({
        type: "success",
        message: "Title updated successfully",
      });

      onClose();
    } catch (err) {
      console.error("Failed to update title:", err);
      setError(err instanceof Error ? err.message : "Failed to update title");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) {
      handleSave();
    }
  };

  return (
    <div className="p-6">
      <div className="space-y-4">
        <div>
          <label
            htmlFor="project-title"
            className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2"
          >
            Project Title
          </label>
          <input
            id="project-title"
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={handleKeyDown}
            placeholder="Enter project title"
            className={INPUT_BASE_STYLE}
            autoFocus
          />
          {error && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="filled" onClick={handleSave} disabled={isLoading}>
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}
